import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import countries from '@/shared/configs/countries.json';

type DeliveryAddressFormProps = {
  deliveryAddressFormFields: Record<string, unknown>;
  onUpdateDeliveryAddress: (values: Record<string, unknown>) => void;
};

const DeliveryAddressForm: FC<DeliveryAddressFormProps> = ({
  deliveryAddressFormFields,
  onUpdateDeliveryAddress,
}) => {
  const { t } = useTranslation(['customers', 'forms']);

  return (
    <WaveUIAccordion
      title={
        <Typography variant='body2' textTransform='uppercase'>
          {t('customers:shippingAddress')}
        </Typography>
      }
      defaultExpanded
      elevation={0}
      sx={{ mx: -2 }}
    >
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Select,
            field_props: {
              name: 'delivery_country',
              label: t('forms:delivery_country'),
              placeholder: t('forms:delivery_country_placeholder'),
              value: deliveryAddressFormFields.delivery_country as string,
              options: countries.map((country) => ({
                label: country.country,
                value: country.code,
              })),
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'delivery_address',
              label: t('forms:delivery_address'),
              placeholder: t('forms:delivery_address_placeholder'),
              value: deliveryAddressFormFields.delivery_address as string,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'delivery_address2',
              label: t('forms:delivery_address2'),
              placeholder: t('forms:delivery_address2_placeholder'),
              value: deliveryAddressFormFields.delivery_address2 as string,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: 'delivery_city',
              label: t('forms:delivery_city'),
              placeholder: t('forms:delivery_city_placeholder'),
              value: deliveryAddressFormFields.delivery_city as string,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Select,
            field_props: {
              name: 'delivery_state',
              label: t('forms:delivery_state'),
              placeholder: t('forms:delivery_state_placeholder'),
              value: deliveryAddressFormFields.delivery_state as string,
              options:
                countries
                  .find(
                    (country) =>
                      country.code ===
                      (deliveryAddressFormFields.delivery_country as string),
                  )
                  ?.provinces.map((province) => ({
                    label: province.name,
                    value: province.code,
                  })) || [],
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'delivery_postcode',
              label: t('forms:delivery_postcode'),
              placeholder: t('forms:delivery_postcode_placeholder'),
              value: deliveryAddressFormFields.delivery_postcode as string,
            },
          },
          {
            field_type: FieldType.Checkbox,
            field_props: {
              name: 'billing_same_as_delivery',
              label: t('forms:billing_same_as_delivery'),
              checked:
                deliveryAddressFormFields.billing_same_as_delivery as boolean,
            },
          },
        ]}
        getFieldsValues={onUpdateDeliveryAddress}
      />
    </WaveUIAccordion>
  );
};

export default DeliveryAddressForm;
